import { t } from 'i18next';
import { orderBy, startCase } from 'lodash-es';
import {
	browserTimeZone,
	convertDroneTagDateToDayJS,
	convertHiddenLevelDateToDayJS,
	dateDisplayTimeMinutesSeconds
} from 'utils/timeHelpers';
import dayjs from 'utils/customDayJS';
import distance from '@turf/distance';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { Box } from '@mui/material';
import { $danger, $success } from 'constants/styles';
import { getLiveTrafficColumnsFromLS, saveLiveTrafficColumnsToLS } from 'actions/localStorage';
import { meterToFt } from 'utils/conversionHelpers';
import {
	droneTagProviderName,
	hiddenLevelProviderName
} from 'components/airSpace/airspaceConstants';

export const getHLAircraftType = track => {
	if (track.properties.adsb_info) {
		return { label: t('ADSB'), value: 'adsb' }; //Airplane icon
	} else if (track.properties.asterix_info) {
		return { label: t('Asterix'), value: 'asterix' }; //Circle with arrow icon
	} else {
		return { label: t('Unknown'), value: 'unknown' }; //Circle with arrow icon
	}
};

const getAMAircraftType = type => {
	switch (type) {
		case 'adsb': {
			return { label: t('ADSB'), value: 'adsb' };
		} //Airplane icon
		case 'asterix': {
			return { label: t('Asterix'), value: 'asterix' };
		} //Circle with arrow icon
		case 'aircraft': {
			return { label: t('Aircraft'), value: 'aircraft' };
		} //Drone icon
		case 'unknown': {
			return { label: t('Unknown'), value: 'unknown' };
		} //Circle with arrow icon
		default: {
			return { label: t('Unknown'), value: 'unknown' };
		} //Circle with arrow icon
	}
};

export const normalizeDTTelemetry = data => {
	return data.map(index => {
		const normalizeProperties = {
			time_of_applicability: convertDroneTagDateToDayJS(index.time, browserTimeZone).format(
				dateDisplayTimeMinutesSeconds
			),
			time_human_readable: convertDroneTagDateToDayJS(index.time, browserTimeZone).format(
				dateDisplayTimeMinutesSeconds
			),
			aircraft_type: 'aircraft',
			aircraft_type_label: t('Aircraft'),
			id: index.uas_id,
			provider: droneTagProviderName,
			provider_id: index.uas_id,
			altitude_msl: index.altitude ? meterToFt(index.altitude) : null,
			heading: null,
			ground_speed: null,
			lat: index.location.latitude,
			long: index.location.longitude,
			seq_number: index.seq_number,
			update: 'in-flight'
		};
		return {
			type: 'Feature',
			geometry: {
				type: 'Point',
				coordinates: [index.location.longitude, index.location.latitude]
			},
			...normalizeProperties,
			properties: normalizeProperties
		};
	});
};

export const normalizeHLData = data => {
	return data.map(index => {
		const normalizeProperties = {
			time_of_applicability: convertHiddenLevelDateToDayJS(
				index.properties.time_of_applicability,
				browserTimeZone
			).format(dateDisplayTimeMinutesSeconds),
			time_human_readable: convertHiddenLevelDateToDayJS(
				index.properties.time_of_applicability,
				browserTimeZone
			).format(dateDisplayTimeMinutesSeconds),
			aircraft_type: getHLAircraftType(index)?.value,
			aircraft_type_label: getHLAircraftType(index)?.label,
			id: index.id,
			provider: hiddenLevelProviderName,
			provider_id: index.properties.human_id,
			altitude: index.properties?.alt_meters_agl
				? meterToFt(index.properties.alt_meters_agl)
				: null,
			heading: index.properties.heading_deg,
			ground_speed: index.properties.ground_speed_mps,
			lat: index.geometry.coordinates[1],
			long: index.geometry.coordinates[0]
		};

		return {
			type: index.type,
			geometry: index.geometry,
			...normalizeProperties,
			properties: normalizeProperties
		};
	});
};

export const getLiveTrafficTableColumns = isAirMesh => {
	const columnsFromLS = getLiveTrafficColumnsFromLS();

	const getWidthFromLS = (colFromLS, colName, defaultWidth) => {
		const col = colFromLS.find(index => index.field === colName);
		return col ? col.width : defaultWidth;
	};

	const airMeshColumns = [
		{
			field: 'timestamp',
			headerName: t('Timestamp'),
			width: 225,
			renderCell: params => {
				return dayjs
					.unix(params.row.timestamp / 1000) //Receiving in microseconds
					.tz(browserTimeZone)
					.format(dateDisplayTimeMinutesSeconds);
			}
		},
		{ field: 'provider_id', headerName: t('Provider ID'), width: 150 },
		{
			field: 'aircraft_type',
			headerName: t('Type'),
			width: 100,
			renderCell: parameters => {
				return getAMAircraftType(parameters.row.aircraft_type)?.label;
			}
		},
		{
			field: 'altitude_agl_ft',
			headerName: t('Altitude (FT AGL)'),
			width: 175,
			renderCell: parameters => {
				const icon =
					parameters.row.altitude_change > 0 ? (
						<ArrowDropUpIcon fontSize='large' sx={{ color: $success }} />
					) : (
						<ArrowDropDownIcon fontSize='large' sx={{ color: $danger }} />
					);
				return (
					parameters.row.altitude_agl_ft && (
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							{parameters.row.altitude_agl_ft} {icon}
						</Box>
					)
				);
			}
		},
		{
			field: 'heading',
			headerName: t('Heading'),
			width: 150,
			renderCell: params => {
				return convertDegreesToCardinalDirection(params.row.heading);
			}
		},
		{
			field: 'ground_speed',
			headerName: t('Ground Speed'),
			width: 175,
			renderCell: params => {
				const isADSB = params.row.aircraft_type === 'adsb';
				return `${params.row.ground_speed} ${isADSB ? t('(MPH)') : t('(MPS)')}`;
			}
		},
		{ field: 'lat', headerName: t('Latitude'), width: 150 },
		{ field: 'long', headerName: t('Longitude'), width: 150 },
		{
			field: 'altitude_msl_ft',
			headerName: t('Altitude (FT MSL)'),
			width: 175,
			renderCell: parameters => {
				const icon =
					parameters.row.altitude_change > 0 ? (
						<ArrowDropUpIcon fontSize='large' sx={{ color: $success }} />
					) : (
						<ArrowDropDownIcon fontSize='large' sx={{ color: $danger }} />
					);
				return (
					parameters.row.altitude_msl_ft && (
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							{parameters.row.altitude_msl_ft} {icon}
						</Box>
					)
				);
			}
		},
		{
			field: 'altitude_rel_ft',
			headerName: t('Altitude (FT REL)'),
			width: 175,
			renderCell: parameters => {
				const icon =
					parameters.row.altitude_change > 0 ? (
						<ArrowDropUpIcon fontSize='large' sx={{ color: $success }} />
					) : (
						<ArrowDropDownIcon fontSize='large' sx={{ color: $danger }} />
					);
				return (
					parameters.row.altitude_rel_ft && (
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							{parameters.row.altitude_rel_ft} {icon}
						</Box>
					)
				);
			}
		},
		{
			field: 'id',
			width: 350,
			headerName: t('Track ID')
		},
		{
			field: 'provider',
			headerName: t('Provider'),
			width: 200,
			renderCell: parameters => {
				return startCase(parameters.row.provider);
			}
		}
	].map(col => {
		const newWidth = getWidthFromLS(columnsFromLS, col.field, col.width);
		return {
			...col,
			width: newWidth
		};
	});

	const hiddenLevelDroneTagColumns = [
		{ field: 'time_human_readable', headerName: t('Timestamp'), width: 225 },
		{
			field: 'aircraft_type_label',
			headerName: t('Type'),
			width: 125
		},
		{
			field: 'altitude',
			headerName: t('Altitude (FT AGL)'),
			width: 200,
			renderCell: parameters => {
				const icon =
					parameters.row.altitude_change > 0 ? (
						<ArrowDropUpIcon fontSize='large' sx={{ color: $success }} />
					) : (
						<ArrowDropDownIcon fontSize='large' sx={{ color: $danger }} />
					);
				return (
					parameters.row.altitude && (
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							{parameters.row.altitude} {icon}
						</Box>
					)
				);
			}
		},
		{
			field: 'heading',
			headerName: t('Heading'),
			width: 150,
			renderCell: params => {
				return convertDegreesToCardinalDirection(params.row.heading);
			}
		},
		{
			field: 'ground_speed',
			headerName: t('Ground Speed'),
			width: 175,
			renderCell: params => {
				const isADSB = params.row.aircraft_type === 'adsb';
				return `${params.row.ground_speed} ${isADSB ? t('(MPH)') : t('(MPS)')}`;
			}
		},
		{ field: 'lat', headerName: t('Latitude'), width: 150 },
		{ field: 'long', headerName: t('Longitude'), width: 150 },
		{
			field: 'id',
			width: 350,
			headerName: t('Track ID')
		},
		{
			field: 'provider',
			headerName: t('Provider'),
			width: 200,
			renderCell: parameters => {
				return startCase(parameters.row.provider);
			}
		},
		{ field: 'provider_id', headerName: t('Provider ID'), width: 350 },
		{
			field: 'altitude_msl',
			headerName: t('Altitude (FT MSL)'),
			width: 200,
			renderCell: parameters => {
				const icon =
					parameters.row.altitude_change > 0 ? (
						<ArrowDropUpIcon fontSize='large' sx={{ color: $success }} />
					) : (
						<ArrowDropDownIcon fontSize='large' sx={{ color: $danger }} />
					);
				return (
					parameters.row.altitude_msl && (
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							{parameters.row.altitude_msl} {icon}
						</Box>
					)
				);
			}
		}
	].map(col => {
		const newWidth = getWidthFromLS(columnsFromLS, col.field, col.width);
		return {
			...col,
			width: newWidth
		};
	});

	if (isAirMesh) {
		return airMeshColumns;
	} else {
		return hiddenLevelDroneTagColumns;
	}
};

export const formatTrafficDataForTable = (data, mapPinLocation, isAirMesh) => {
	const dataWithDistanceFromPin = data.map(index => {
		return {
			...index,
			distanceFromPin: distance(index.geometry.coordinates, mapPinLocation.geometry.coordinates)
		};
	});

	const orderedData = orderBy(dataWithDistanceFromPin, ['distanceFromPin'], ['asc']);

	const columns = getLiveTrafficTableColumns(isAirMesh);

	saveLiveTrafficColumnsToLS(columns);

	return {
		columns,
		rows: orderedData
	};
};

export const createTrafficGeoJsonSource = features => {
	return {
		type: 'geojson',
		data: {
			type: 'FeatureCollection',
			features
		}
	};
};

export const formatDTDataIntoFeatures = data => {
	return data.map(index => {
		let newFeature = {
			type: 'Feature',
			geometry: {
				type: 'Point',
				coordinates: [index.location.longitude, index.location.latitude]
			},
			properties: {
				...index,
				lat: index.location.latitude,
				long: index.location.longitude,
				velocity_x: index.velocity.x,
				velocity_y: index.velocity.y,
				velocity_z: index.velocity.z
			}
		};
		delete newFeature.properties.location;
		delete newFeature.properties.velocity;
		return newFeature;
	});
};

const removeIntFromProvider = string => {
	//Example: removes '_12' from 'airmesh_sdk_12'
	return string.replace(/_\d+/g, '');
};

export const normalizeAMData = data => {
	return data.map(index => {
		const normalizeProperties = {
			time_of_applicability: dayjs(index.observed_timestamp)
				.tz(browserTimeZone)
				.format(dateDisplayTimeMinutesSeconds),
			timestamp: index.observed_timestamp,
			aircraft_type: index.type,
			id: index.track_id,
			provider: removeIntFromProvider(index.provider),
			provider_id: index.provider_id_human,
			altitude: meterToFt(index.altitude_agl_mt) || meterToFt(index.altitude_msl_mt),
			altitude_agl_ft: meterToFt(index.altitude_agl_mt),
			altitude_msl_ft: meterToFt(index.altitude_msl_mt),
			altitude_rel_ft: meterToFt(index.altitude_rel_mt),
			heading: index.heading_deg,
			ground_speed: index.ground_speed_mps,
			lat: index.geometry.coordinates[1],
			long: index.geometry.coordinates[0]
		};

		return {
			type: 'Feature',
			geometry: index.geometry,
			...normalizeProperties,
			properties: normalizeProperties
		};
	});
};

export const removeExpiredTrafficFeatures = features => {
	return features.filter(f => {
		const now = dayjs();
		let timeFrame = 0;
		if (f.provider === 'Drone Tag' || f.provider === 'Hidden Level') {
			timeFrame = now.subtract(60, 'second');
		} else {
			//Air Mesh
			timeFrame = now.subtract(30, 'second');
		}
		const featureDate = dayjs(f.properties.time_of_applicability);
		return featureDate.isAfter(timeFrame);
	});
};

export const convertDegreesToCardinalDirection = degrees => {
	// Define cardinal directions
	const cardinalDirections = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW', 'N'];

	// Convert degrees to cardinal direction
	// Divide the degrees by 45 and round to the nearest integer to determine the index
	// Using Math.floor ensures that the result is always an integer representing the lower bound
	const index = Math.floor(degrees / 45 + 0.5) % 8;

	return cardinalDirections[index];
};
